import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Layout from '../components/Layout';
import PageTabBar from '../components/PageTabBar';
import StockSatelliteHeader from '../features/stock/StockSatelliteHeader';
import StockMagasinPicker from '../features/stock/StockMagasinPicker';
import { STOCK_PANEL, STOCK_BTN_PRIMARY, STOCK_BTN_SECONDARY, StockFormSection } from '../features/stock/stockV2Ui';
import StockImeiTraceSection from '../features/stock/StockImeiTraceSection';
import StockProductReviewModal from '../features/stock/StockProductReviewModal';
import { analyzeStockLines, classifyArticleHeuristic, registerImeisForArticle } from '../features/stock/stockProductClassify';
import { STOCK_TABS } from './_stockTabs';
import { Save, Package, Barcode, DollarSign, Tag, Truck, ShoppingBag, Search, Loader2, Store, Warehouse, RefreshCw, Camera, Boxes, ScanLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import BarcodeScannerModal from '../components/BarcodeScannerModal';

const CURRENCY_FLAGS = { EUR: '🇪🇺', USD: '🇺🇸', BRL: '🇧🇷', COP: '🇨🇴' };

const StockFormPage = () => {
  const { api, user } = useAuth();
  const { t, tError, formatCurrency, formatNumber } = useLanguage();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierResults, setSupplierResults] = useState([]);
  const [searchingSupplier, setSearchingSupplier] = useState(false);
  const [formData, setFormData] = useState({
    reference: '',
    ean: '',
    designation: '',
    prix_achat: 0,
    prix_vente: 0,
    quantite: 0,
    quantite_depot: 0,
    categorie: '',
    fournisseur: '',
    seuil_alerte: 3,
    visible_boutique: false
  });
  const [stockLocation, setStockLocation] = useState('boutique');
  const [currencies, setCurrencies] = useState([]);
  const [rates, setRates] = useState({ EUR: 1 });
  const [achatCurrency, setAchatCurrency] = useState('EUR');
  const [achatOriginal, setAchatOriginal] = useState(0);
  const [existingArticle, setExistingArticle] = useState(null);
  const [checkingRef, setCheckingRef] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [designSearch, setDesignSearch] = useState([]);
  const [showDesignSearch, setShowDesignSearch] = useState(false);
  const [magasinId, setMagasinId] = useState(() => {
    try {
      return localStorage.getItem('pos_current_magasin_id') || user?.default_magasin_id || 'M1';
    } catch {
      return user?.default_magasin_id || 'M1';
    }
  });
  const [magasinsList, setMagasinsList] = useState([]);
  const [deviceImei, setDeviceImei] = useState('');
  const [imeiError, setImeiError] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewLines, setReviewLines] = useState([]);
  const [reviewAnalyzing, setReviewAnalyzing] = useState(false);
  const refCheckTimer = React.useRef(null);
  const designSearchTimer = React.useRef(null);

  useEffect(() => {
    if (isEditing) {
      fetchArticle();
    }
  }, [id]);

  useEffect(() => {
    api.get('/exchange-rates').then(r => {
      setCurrencies(r.data.currencies || []);
      setRates(r.data.rates || { EUR: 1 });
    }).catch(() => {});
  }, [api]);

  useEffect(() => {
    api.get('/magasins?active_only=true')
      .then((r) => setMagasinsList(r.data?.magasins || []))
      .catch(() => setMagasinsList([]));
  }, [api]);

  useEffect(() => {
    if (magasinId !== 'M1' && stockLocation === 'depot') {
      setStockLocation('boutique');
    }
  }, [magasinId, stockLocation]);

  const handleMagasinChange = (code) => {
    setMagasinId(code);
    try { localStorage.setItem('pos_current_magasin_id', code); } catch { /* ignore */ }
  };

  const margePct = formData.prix_achat > 0
    ? Math.round(((formData.prix_vente - formData.prix_achat) / formData.prix_achat) * 100)
    : 0;

  const classifyLocal = classifyArticleHeuristic(formData.categorie, formData.designation, formData.reference);
  const showImeiSection = !isEditing && classifyLocal.needsImei;

  const validatePhoneImei = (needsImei = false) => {
    if (!needsImei || isEditing) return true;
    const imei = deviceImei.trim().replace(/\D/g, '');
    if (!imei) {
      setImeiError(t('stockForm.imeiRequired'));
      return false;
    }
    if (imei.length < 14 || imei.length > 20) {
      setImeiError(t('stockForm.imeiInvalid'));
      return false;
    }
    setImeiError('');
    return true;
  };

  const convertToEur = (amount, currency) => {
    const rate = rates[currency] || 1;
    return rate === 1 ? amount : +(amount / rate).toFixed(2);
  };

  const checkExistingRef = useCallback((ref) => {
    if (!ref || ref.length < 2 || isEditing) return;
    if (refCheckTimer.current) clearTimeout(refCheckTimer.current);
    refCheckTimer.current = setTimeout(async () => {
      setCheckingRef(true);
      try {
        const r = await api.get(`/stock/by-reference?ref=${encodeURIComponent(ref)}&magasin_id=${encodeURIComponent(magasinId || 'M1')}`);
        if (r.data.found) {
          const a = r.data.article;
          setExistingArticle(a);
          setFormData(prev => ({
            ...prev,
            ean: a.ean || prev.ean,
            designation: a.designation || prev.designation,
            prix_achat: a.prix_achat || prev.prix_achat,
            prix_vente: a.prix_vente || prev.prix_vente,
            categorie: a.categorie || prev.categorie,
            fournisseur: a.fournisseur || prev.fournisseur,
            seuil_alerte: a.seuil_alerte ?? prev.seuil_alerte,
            visible_boutique: a.visible_boutique ?? prev.visible_boutique,
            quantite: 0,
            quantite_depot: 0,
          }));
          setAchatOriginal(a.prix_achat || 0);
        } else {
          setExistingArticle(null);
        }
      } catch { setExistingArticle(null); }
      setCheckingRef(false);
    }, 500);
  }, [api, isEditing, magasinId]);

  const handleBarcodeScan = async (code) => {
    setShowScanner(false);
    try {
      const r = await api.get(`/stock/by-reference?ean=${encodeURIComponent(code)}`);
      if (r.data.found) {
        const a = r.data.article;
        setExistingArticle(a);
        setFormData(prev => ({
          ...prev,
          reference: a.reference || prev.reference,
          ean: a.ean || code,
          designation: a.designation || prev.designation,
          prix_achat: a.prix_achat || prev.prix_achat,
          prix_vente: a.prix_vente || prev.prix_vente,
          categorie: a.categorie || prev.categorie,
          fournisseur: a.fournisseur || prev.fournisseur,
          seuil_alerte: a.seuil_alerte ?? prev.seuil_alerte,
          visible_boutique: a.visible_boutique ?? prev.visible_boutique,
          quantite: 0,
          quantite_depot: 0,
        }));
        setAchatOriginal(a.prix_achat || 0);
      } else {
        setFormData(prev => ({ ...prev, ean: code }));
        setExistingArticle(null);
      }
    } catch {
      setFormData(prev => ({ ...prev, ean: code }));
    }
  };

  const searchByDesignation = useCallback((query) => {
    if (!query || query.length < 2 || isEditing) { setDesignSearch([]); setShowDesignSearch(false); return; }
    if (designSearchTimer.current) clearTimeout(designSearchTimer.current);
    designSearchTimer.current = setTimeout(async () => {
      try {
        const r = await api.get(`/stock/search/${encodeURIComponent(query)}`);
        if (r.data && r.data.length > 0) {
          setDesignSearch(r.data.slice(0, 8));
          setShowDesignSearch(true);
        } else {
          setDesignSearch([]);
          setShowDesignSearch(false);
        }
      } catch { setDesignSearch([]); setShowDesignSearch(false); }
    }, 300);
  }, [api, isEditing]);

  const selectFromSearch = (article) => {
    setExistingArticle(article);
    setFormData(prev => ({
      ...prev,
      reference: article.reference || prev.reference,
      ean: article.ean || prev.ean,
      designation: article.designation || prev.designation,
      prix_achat: article.prix_achat || prev.prix_achat,
      prix_vente: article.prix_vente || prev.prix_vente,
      categorie: article.categorie || prev.categorie,
      fournisseur: article.fournisseur || prev.fournisseur,
      seuil_alerte: article.seuil_alerte ?? prev.seuil_alerte,
      visible_boutique: article.visible_boutique ?? prev.visible_boutique,
      quantite: 0,
      quantite_depot: 0,
    }));
    setAchatOriginal(article.prix_achat || 0);
    setDesignSearch([]);
    setShowDesignSearch(false);
  };

  const fetchArticle = async () => {
    try {
      const response = await api.get(`/stock/${id}`);
      setFormData(response.data);
      setAchatOriginal(response.data.prix_achat || 0);
      setAchatCurrency(response.data.achat_currency || 'EUR');
      const qBoutique = response.data.quantite || 0;
      const qDepot = response.data.quantite_depot || 0;
      if (qBoutique > 0 && qDepot > 0) setStockLocation('both');
      else if (qDepot > 0) setStockLocation('depot');
      else setStockLocation('boutique');
    } catch (error) {
      alert(t('stockForm.articleNotFound'));
      navigate('/stock');
    }
  };

  const searchSupplierParts = useCallback(async (query) => {
    if (!query || query.length < 2) { setSupplierResults([]); return; }
    setSearchingSupplier(true);
    try {
      const res = await api.get(`/supplier-parts/search?query=${encodeURIComponent(query)}&limit=8`);
      setSupplierResults(res.data?.results || []);
    } catch { setSupplierResults([]); }
    finally { setSearchingSupplier(false); }
  }, [api]);

  useEffect(() => {
    const timer = setTimeout(() => searchSupplierParts(supplierSearch), 400);
    return () => clearTimeout(timer);
  }, [supplierSearch, searchSupplierParts]);

  const fillFromSupplier = (part) => {
    setFormData(prev => ({
      ...prev,
      reference: part.reference || part.ref || prev.reference,
      ean: part.ean || part.barcode || prev.ean,
      designation: part.designation || part.part_name || part.name || part.title || prev.designation,
      prix_achat: part.prix_achat || part.price || part.prix || prev.prix_achat,
      fournisseur: part.fournisseur || part.supplier || prev.fournisseur,
      categorie: part.categorie || part.category || prev.categorie,
    }));
    setSupplierSearch('');
    setSupplierResults([]);
    const ref = part.reference || part.ref || '';
    if (ref) checkExistingRef(ref);
  };

  const doSave = async (needsImei = classifyLocal.needsImei) => {
    if (needsImei && !validatePhoneImei(needsImei)) {
      setLoading(false);
      return;
    }
    setLoading(true);

    const submitData = {
      ...formData,
      achat_currency: achatCurrency,
      achat_original: achatOriginal,
      magasin_id: magasinId,
      categorie: formData.categorie || classifyLocal.suggestedCategorie || '',
    };
    let ref = submitData.reference;
    if (stockLocation === 'boutique') submitData.quantite_depot = 0;
    if (stockLocation === 'depot') { submitData.quantite_depot = submitData.quantite_depot || formData.quantite_depot; submitData.quantite = 0; }

    try {
      if (isEditing) {
        await api.put(`/stock/${id}`, submitData);
      } else if (existingArticle) {
        let addQty;
        if (stockLocation === 'depot') {
          addQty = { quantite: existingArticle.quantite || 0, quantite_depot: (existingArticle.quantite_depot || 0) + (submitData.quantite_depot || 0) };
        } else if (stockLocation === 'both') {
          addQty = { quantite: (existingArticle.quantite || 0) + (submitData.quantite || 0), quantite_depot: (existingArticle.quantite_depot || 0) + (submitData.quantite_depot || 0) };
        } else {
          addQty = { quantite: (existingArticle.quantite || 0) + (submitData.quantite || 0), quantite_depot: existingArticle.quantite_depot || 0 };
        }
        await api.put(`/stock/${existingArticle.id}`, { ...submitData, ...addQty });
        ref = existingArticle.reference || ref;
      } else {
        await api.post('/stock', submitData);
      }

      if (needsImei) {
        const imeiList = deviceImei.trim() ? [deviceImei] : [];
        const ok = imeiList.length ? await registerImeisForArticle(api, ref, imeiList, magasinId) : 0;
        if (!ok && !isEditing) {
          setImeiError(t('stockForm.imeiNotRegistered'));
          setLoading(false);
          return;
        }
        const qtyAdded = (stockLocation === 'depot')
          ? (submitData.quantite_depot || 0)
          : (stockLocation === 'both')
            ? (submitData.quantite || 0) + (submitData.quantite_depot || 0)
            : (submitData.quantite || 0);
        if (!isEditing && qtyAdded > 1) {
          window.alert(t('stockForm.imeiFirstRegistered', { count: qtyAdded - 1, ref }));
        }
      }

      setReviewOpen(false);
      navigate('/stock');
    } catch (error) {
      alert(tError(error.response?.data?.detail) || t('stockForm.saveError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isEditing) {
      await doSave(false);
      return;
    }

    setReviewAnalyzing(true);
    setReviewOpen(true);
    try {
      const data = await analyzeStockLines(api, [{
        reference: formData.reference,
        designation: formData.designation,
        categorie: formData.categorie,
        quantite: formData.quantite || 1,
        prix_vente: formData.prix_vente,
        prix_achat: formData.prix_achat,
        imei_list: deviceImei ? [deviceImei] : [],
      }]);
      const ln = data.lines?.[0];
      setReviewLines(data.lines || []);
      if (ln?.suggested_categorie && !formData.categorie) {
        setFormData((prev) => ({ ...prev, categorie: ln.suggested_categorie }));
      }
      const needsImei = !!ln?.needs_imei;
      if (data.can_save_all) {
        setReviewOpen(false);
        await doSave(needsImei);
      }
    } catch {
      setReviewOpen(false);
      await doSave(classifyLocal.needsImei);
    } finally {
      setReviewAnalyzing(false);
    }
  };

  const handleReviewLineChange = (index, patch) => {
    setReviewLines((prev) => prev.map((ln) => (ln.index === index ? { ...ln, ...patch } : ln)));
    if (patch.designation != null) setFormData((prev) => ({ ...prev, designation: patch.designation }));
    if (patch.categorie != null) setFormData((prev) => ({ ...prev, categorie: patch.categorie }));
    if (patch.imei_list?.[0] != null) setDeviceImei(patch.imei_list[0]);
  };

  const handleReviewConfirm = async () => {
    const ln = reviewLines[0];
    if (!ln || ln.can_save === false) return;
    if (ln.categorie) setFormData((prev) => ({ ...prev, categorie: ln.categorie }));
    if (ln.imei_list?.[0]) setDeviceImei(ln.imei_list[0]);
    await doSave(!!ln.needs_imei);
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : type === 'number' ? parseFloat(value) || 0 : value
    });
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto fade-in space-y-4" data-testid="stock-form-page">
        <PageTabBar tabs={STOCK_TABS} activeColor="rose" />
        <StockSatelliteHeader
          title={isEditing ? t('stockForm.editTitle') : t('stockForm.addTitle')}
          subtitle={isEditing ? formData.reference || t('stockForm.productSheet') : t('stockForm.newStockProduct')}
          backTestId="stock-form-back"
        />

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isEditing && (
            <div className={`${STOCK_PANEL} p-4 flex flex-wrap gap-3 items-start`}>
              <Link to="/stock/analyser" className={STOCK_BTN_SECONDARY} data-testid="stock-analyze-link">
                <ScanLine className="w-5 h-5" /> {t('stockForm.analyzePhoto')}
              </Link>
              <button type="button" onClick={() => setShowScanner(true)} className={STOCK_BTN_SECONDARY} data-testid="open-scanner-btn">
                <Camera className="w-5 h-5" /> {t('stockForm.scan')}
              </button>
              <div className="relative flex-1 min-w-[240px]" data-testid="supplier-search-section">
                <label className="block text-xs text-gray-400 mb-2 font-semibold uppercase">{t('stockForm.supplierSearch')}</label>
                <div className="relative">
                  <input
                    type="text"
                    value={supplierSearch}
                    onChange={(e) => setSupplierSearch(e.target.value)}
                    className="input-dark w-full pl-10 py-2.5"
                    placeholder={t('stockForm.supplierSearchPlaceholder')}
                    data-testid="supplier-search-input"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                  {searchingSupplier && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400 animate-spin" />}
                </div>
                {supplierResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-[#151820] border border-white/10 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                    {supplierResults.map((part, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => fillFromSupplier(part)}
                        className="w-full px-4 py-3 text-left hover:bg-red-950/20 border-b border-white/5 last:border-0 transition-colors"
                        data-testid={`supplier-result-${idx}`}
                      >
                        <div className="text-sm text-white font-medium truncate">{part.designation || part.part_name || part.name || part.title}</div>
                        <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                          {(part.reference || part.ref) && <span>{t('stockForm.refShort')} {part.reference || part.ref}</span>}
                          {(part.prix_achat || part.price || part.prix) && <span>{t('stockForm.priceLabel')} {formatCurrency(part.prix_achat || part.price || part.prix, 'EUR')}</span>}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <StockFormSection title={t('stockForm.sectionArticle')} icon={Tag}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.reference')} *</label>
                  <div className="relative">
                    <input type="text" name="reference" value={formData.reference}
                      onChange={(e) => { handleChange(e); checkExistingRef(e.target.value); }}
                      className="input-dark w-full pl-10 py-2.5" required disabled={isEditing} />
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    {checkingRef && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400 animate-spin" />}
                  </div>
                  {existingArticle && !isEditing && (
                    <div className="mt-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center gap-2" data-testid="existing-article-banner">
                      <Package className="w-4 h-4 text-amber-400 shrink-0" />
                      <span className="text-xs text-amber-200">
                        {t('stockForm.existingBanner', {
                          designation: existingArticle.designation || '',
                          qb: existingArticle.quantite || 0,
                          qd: existingArticle.quantite_depot || 0,
                        })}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.ean')}</label>
                  <div className="relative">
                    <input type="text" name="ean" value={formData.ean} onChange={handleChange} className="input-dark w-full pl-10 py-2.5" placeholder={t('stockForm.eanPlaceholder')} />
                    <Barcode className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.category')}</label>
                  <input type="text" name="categorie" value={formData.categorie} onChange={handleChange} className="input-dark w-full py-2.5" placeholder={t('stockForm.categoryPlaceholder')} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.designation')} *</label>
                  <div className="relative">
                    <input type="text" name="designation" value={formData.designation}
                      onChange={(e) => { handleChange(e); searchByDesignation(e.target.value); }}
                      onBlur={() => setTimeout(() => setShowDesignSearch(false), 200)}
                      className="input-dark w-full pl-10 py-2.5" required data-testid="designation-input" />
                    <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                    {showDesignSearch && designSearch.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-[#151820] border border-white/10 rounded-xl shadow-2xl max-h-56 overflow-y-auto" data-testid="designation-search-dropdown">
                        {designSearch.map((a, i) => (
                          <button key={a.id || i} type="button" onClick={() => selectFromSearch(a)}
                            className="w-full px-4 py-3 text-left hover:bg-red-950/20 border-b border-white/5 last:border-0 transition-colors"
                            data-testid={`designation-result-${i}`}>
                            <div className="text-sm text-white font-medium truncate">{a.designation}</div>
                            <div className="flex gap-3 text-xs text-gray-500 mt-0.5">
                              <span>{t('stockForm.refShort')} {a.reference}</span>
                              <span>{formatCurrency(a.prix_vente || 0, 'EUR')}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.supplier')}</label>
                  <div className="relative">
                    <input type="text" name="fournisseur" value={formData.fournisseur} onChange={handleChange} className="input-dark w-full pl-10 py-2.5" />
                    <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  </div>
                </div>
              </div>
            </StockFormSection>

            <StockFormSection title={t('stockForm.sectionStock')} icon={Boxes}>
              <StockMagasinPicker magasinsList={magasinsList} value={magasinId} onChange={handleMagasinChange} compact />
              <div className="mt-4">
                <label className="block text-xs text-gray-400 mb-2">{t('stockForm.location')}</label>
                <div className="flex flex-wrap gap-2 mb-4">
                  <button type="button" onClick={() => setStockLocation('boutique')}
                    className={`flex-1 min-w-[100px] min-h-[44px] px-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                      stockLocation === 'boutique' ? 'border-emerald-500/50 bg-emerald-950/30 text-emerald-300' : 'border-white/10 text-gray-400 hover:border-white/20'
                    }`} data-testid="stock-location-boutique">
                    <Store className="w-4 h-4" /> {t('stockForm.shop')}
                  </button>
                  <button type="button" onClick={() => magasinId === 'M1' && setStockLocation('depot')}
                    disabled={magasinId !== 'M1'}
                    className={`flex-1 min-w-[100px] min-h-[44px] px-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                      stockLocation === 'depot' ? 'border-blue-500/50 bg-blue-950/30 text-blue-300' : 'border-white/10 text-gray-400 hover:border-white/20'
                    } ${magasinId !== 'M1' ? 'opacity-40 cursor-not-allowed' : ''}`} data-testid="stock-location-depot">
                    <Warehouse className="w-4 h-4" /> {t('stockForm.depot')}
                  </button>
                  <button type="button" onClick={() => setStockLocation('both')}
                    className={`flex-1 min-w-[100px] min-h-[44px] px-3 rounded-lg border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                      stockLocation === 'both' ? 'border-red-500/50 bg-red-950/30 text-red-300' : 'border-white/10 text-gray-400 hover:border-white/20'
                    }`} data-testid="stock-location-both">
                    <Package className="w-4 h-4" /> {t('stockForm.both')}
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {(stockLocation === 'boutique' || stockLocation === 'both') && (
                    <div>
                      <label className="block text-xs text-emerald-400/80 mb-1">{t('stockForm.qtyShop')}</label>
                      <input type="number" name="quantite" value={formData.quantite} onChange={handleChange}
                        className="input-dark w-full py-2.5 text-center font-bold" min="0" data-testid="stock-qty-boutique" />
                    </div>
                  )}
                  {(stockLocation === 'depot' || stockLocation === 'both') && (
                    <div>
                      <label className="block text-xs text-blue-400/80 mb-1">{t('stockForm.qtyDepot')}</label>
                      <input type="number" name="quantite_depot" value={formData.quantite_depot} onChange={handleChange}
                        className="input-dark w-full py-2.5 text-center font-bold" min="0" data-testid="stock-qty-depot" />
                    </div>
                  )}
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">{t('stockForm.alertThreshold')}</label>
                    <input type="number" name="seuil_alerte" value={formData.seuil_alerte} onChange={handleChange}
                      className="input-dark w-full py-2.5 text-center" min="0" />
                  </div>
                </div>
              </div>
            </StockFormSection>

            <StockFormSection title={t('stockForm.sectionPricing')} icon={DollarSign}>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.purchasePrice')}</label>
                  <div className="flex gap-2">
                    <select value={achatCurrency} onChange={(e) => {
                      const cur = e.target.value;
                      setAchatCurrency(cur);
                      setFormData((prev) => ({ ...prev, prix_achat: convertToEur(achatOriginal, cur) }));
                    }} className="input-dark py-2.5 px-2 w-[100px] text-sm" data-testid="currency-selector">
                      {currencies.map((c) => <option key={c.code} value={c.code}>{CURRENCY_FLAGS[c.code]} {c.code}</option>)}
                      {currencies.length === 0 && <option value="EUR">🇪🇺 EUR</option>}
                    </select>
                    <input type="number" value={achatOriginal}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value) || 0;
                        setAchatOriginal(val);
                        setFormData((prev) => ({ ...prev, prix_achat: convertToEur(val, achatCurrency) }));
                      }}
                      className="input-dark flex-1 py-2.5 text-center" step="0.01" min="0" data-testid="prix-achat-original" />
                  </div>
                  {achatCurrency !== 'EUR' && achatOriginal > 0 && (
                    <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
                      <RefreshCw className="w-3 h-3" /> = {formatCurrency(formData.prix_achat, 'EUR')}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-400 mb-1.5">{t('stockForm.salePrice')} *</label>
                  <input type="number" name="prix_vente" value={formData.prix_vente} onChange={handleChange}
                    className="input-dark w-full py-2.5 text-center font-bold" step="0.01" min="0" required />
                </div>
                <div className={`rounded-lg border border-white/10 bg-black/20 px-3 py-2 text-sm flex justify-between`}>
                  <span className="text-gray-500">{t('stockForm.margin')}</span>
                  <span className="text-white font-bold">{formatNumber(margePct)} %</span>
                </div>
              </div>
            </StockFormSection>

            <StockFormSection title={t('stockForm.sectionShop')} icon={ShoppingBag}>
              <label className="flex items-center gap-3 cursor-pointer p-3 rounded-lg border border-white/10 bg-black/20 hover:border-white/20 transition-colors">
                <input type="checkbox" name="visible_boutique" checked={formData.visible_boutique} onChange={handleChange} className="w-5 h-5 rounded accent-red-600" />
                <div>
                  <div className="font-semibold text-white text-sm">{t('stockForm.visibleOnline')}</div>
                  <div className="text-xs text-gray-500">{t('stockForm.visibleOnlineHint')}</div>
                </div>
              </label>
            </StockFormSection>
          </div>

          {showImeiSection && (
            <div className="space-y-2">
              <StockImeiTraceSection
                imei={deviceImei}
                onImeiChange={(v) => { setDeviceImei(v); if (imeiError) setImeiError(''); }}
                reference={formData.reference}
                magasinId={magasinId}
                required
              />
              {imeiError && (
                <p className="text-xs text-red-400 font-semibold" data-testid="stock-imei-error">{imeiError}</p>
              )}
            </div>
          )}

          <div className={`${STOCK_PANEL} p-4 flex flex-col sm:flex-row gap-3`}>
            <button type="button" onClick={() => navigate('/stock')} className={`${STOCK_BTN_SECONDARY} flex-1 py-3`}>
              {t('stockForm.cancel')}
            </button>
            <button type="submit" disabled={loading || !magasinId} className={`${STOCK_BTN_PRIMARY} flex-1 py-3`}>
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
              {loading ? t('stockForm.saving') : isEditing ? t('stockForm.edit') : existingArticle ? t('stockForm.addToStock') : t('stockForm.save')}
            </button>
          </div>
        </form>
      </div>
      <StockProductReviewModal
        open={reviewOpen}
        title={t('stockForm.reviewTitle')}
        subtitle={formData.reference || t('stockForm.newArticle')}
        lines={reviewLines}
        analyzing={reviewAnalyzing}
        saving={loading}
        onClose={() => { if (!loading) setReviewOpen(false); }}
        onConfirm={handleReviewConfirm}
        onLineChange={handleReviewLineChange}
      />
      <BarcodeScannerModal isOpen={showScanner} onClose={() => setShowScanner(false)} onScan={handleBarcodeScan} />
    </Layout>
  );
};

export default StockFormPage;
